import { useEffect, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useNavigate } from 'react-router-dom'

import { getReturnUrl } from '@/lib/auth/return-url'
import { determineSystemSelection } from '@/lib/auth/system-selection'
import { decodeToken } from '@/lib/auth/token'
import type { User } from '@/models/domain/user'
import { useAuthStore } from '@/stores/auth-store'

function extractKeycloakClaims(resourceAccess: Record<string, { roles?: string[] }>): string[] {
  const keycloakClaims: string[] = []
  for (const clientData of Object.values(resourceAccess)) {
    const roles = clientData.roles ?? []
    keycloakClaims.push(...roles)
  }
  return keycloakClaims
}

function buildUserFromClaims(
  claims: Record<string, unknown>,
  realmRoles: string[],
  keycloakClaims: string[]
): User {
  return {
    email: (claims.email as string) || '',
    first_name: (claims.given_name as string) || '',
    last_name: (claims.family_name as string) || '',
    active: true,
    locked: false,
    sys_admin: realmRoles.includes('cs_sys_admin'),
    sys_op: realmRoles.includes('cs_sys_op'),
    systems: (claims.systems as string[] | null) ?? null,
    systems_all: realmRoles.includes('cs_systems_all'),
    dbas: (claims.dbas as string[] | null) ?? null,
    dbas_all: realmRoles.includes('cs_dbas_all'),
    districts: (claims.districts as string[] | null) ?? null,
    districts_all: realmRoles.includes('cs_districts_all'),
    locations: (claims.locations as string[] | null) ?? null,
    locations_all: realmRoles.includes('cs_locations_all'),
    sections: (claims.sections as string[] | null) ?? null,
    sections_all: realmRoles.includes('cs_sections_all'),
    keycloak_claims: keycloakClaims,
  }
}

function storeTokenAndBuildUser(accessToken: string, expiresIn: number): User | null {
  localStorage.setItem('auth_token', accessToken)
  const expiryTime = Date.now() + expiresIn * 1000
  localStorage.setItem('auth_token_expiry', expiryTime.toString())

  const claims = decodeToken(accessToken)
  if (!claims) return null

  const realmRoles =
    ((claims.realm_access as Record<string, unknown> | undefined)?.roles as string[] | undefined) ??
    []
  const resourceAccess =
    (claims.resource_access as Record<string, { roles?: string[] }> | undefined) ?? {}
  const keycloakClaims = extractKeycloakClaims(resourceAccess)
  return buildUserFromClaims(claims, realmRoles, keycloakClaims)
}

function navigateAfterAuth(
  user: User,
  selectSystem: (system: string) => void,
  navigate: (path: string, options?: { state?: Record<string, unknown> }) => void,
  returnUrl: string
) {
  const { selectedSystem, needsSelection } = determineSystemSelection(user)

  if (selectedSystem) {
    selectSystem(selectedSystem)
    navigate(returnUrl)
    return
  }

  if (needsSelection) {
    navigate('/select-system', { state: { returnUrl } })
    return
  }

  navigate(returnUrl)
}

function CallbackErrorView({
  error,
  onRetry,
  onBackToLogin,
}: {
  error: string
  onRetry: () => void
  onBackToLogin: () => void
}) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full">
            <svg
              className="w-6 h-6 text-red-600 dark:text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-center text-gray-900 dark:text-gray-100 mb-2">
            Authentication Error
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">{error}</p>
          <div className="flex gap-3">
            <button
              onClick={onBackToLogin}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Back to Login
            </button>
            <button
              onClick={onRetry}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function CallbackLoadingView({ isProcessing }: { isProcessing: boolean }) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
          <div className="w-16 h-16 border-4 border-primary-200 dark:border-primary-800 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {isProcessing ? 'Completing authentication...' : 'Redirecting...'}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">Please wait</p>
      </div>
    </div>
  )
}

export function CallbackPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const { setUser, selectSystem } = useAuthStore()
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(true)

  const returnUrl = getReturnUrl(auth.user?.state)

  useEffect(() => {
    const processCallback = async () => {
      if (auth.error) {
        setError(auth.error.message || 'Authentication failed')
        setIsProcessing(false)
        return
      }

      if (!auth.isAuthenticated || !auth.user?.access_token) {
        setIsProcessing(false)
        return
      }

      try {
        const user = storeTokenAndBuildUser(auth.user.access_token, auth.user.expires_in ?? 3600)
        if (!user) {
          setError('Failed to decode access token')
          setIsProcessing(false)
          return
        }

        setUser(user)
        navigateAfterAuth(user, selectSystem, navigate, returnUrl)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to process authentication'
        setError(errorMessage)
        setIsProcessing(false)
      }
    }

    if (auth.isLoading) {
      return
    }

    void processCallback()
  }, [auth, navigate, setUser, selectSystem, returnUrl])

  const handleRetry = () => {
    setError(null)
    setIsProcessing(true)
    void auth.signinRedirect({ state: { returnUrl } })
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  if (error) {
    return (
      <CallbackErrorView error={error} onRetry={handleRetry} onBackToLogin={handleBackToLogin} />
    )
  }

  return <CallbackLoadingView isProcessing={isProcessing} />
}
